import { selectHistoryDocuments, projectHistoryDocument } from './historyEvidence';

export function createEmptySectionData(section) {
  switch (section.type) {
    case "EditableTextSection":
      return {
        summary: "",
        humanReview: false,
        processingStatus: false
      };

    case "EditableTableSection":
      // For data-style sections (like mental status exam)
      if (section.editorStyle === "data" && section.fields) {
        const emptyData = {};
        // Create empty object with all fields
        Object.keys(section.fields).forEach(fieldKey => {
          emptyData[fieldKey] = "";
        });
        return {
          data: emptyData,
          humanReview: false,
          processingStatus: false
        };
      }
      // For table-style sections (like medications)
      return {
        data: [],
        humanReview: false,
        processingStatus: false
      };

    case "StartEndTime":
      return {
        documentDate: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
        startTime: "",
        endTime: ""
      };

    case "TherapyTiming":
      return {
        therapyMinutes: "",
        therapyType: ""
      };

    default:
      return {
        summary: "",
        humanReview: false,
        processingStatus: false
      };
  }
}

/**
 * Create empty sections for a document type
 * @param {Array} documentSections - Array of section definitions from document type
 * @returns {Object} Object with empty data for all sections
 */
export function createEmptySections(documentSections) {
  const emptySections = {};

  if (!documentSections || !Array.isArray(documentSections)) {
    console.log('[createEmptySections] No sections provided or sections is not an array');
    return emptySections;
  }

  documentSections.forEach(section => {
    // Add null check for section object
    if (!section) {
      console.warn('[createEmptySections] Encountered null/undefined section');
      return;
    }

    // Check if section.dataField exists before accessing it
    if (section.dataField && section.type !== "StartEndTime" && section.type !== "TherapyTiming") {
      const emptyData = createEmptySectionData(section);
      if (emptyData !== null) {
        emptySections[section.dataField] = emptyData;
      }
    } else if (!section.dataField && section.type !== "StartEndTime" && section.type !== "TherapyTiming") {
      // Log warning for sections missing dataField
      console.warn('[createEmptySections] Section missing dataField:', section);
    }
  });

  return emptySections;
}


export function getMimeType(fileName) {
  const extension = fileName.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return "application/pdf";
    case "tiff":
      return "image/tiff";
    case "tif":
      return "image/tiff";
    case "jpeg":
      return "image/jpeg";
    case "jpg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    default:
      return null;
  }
}

export function createInitialDocumentStructure(option, selectedDocumentType, selectedDocumentTypeId, selectedCategoryId, startTime, documentSections = null) {
  const descriptions = {
    text: "Text Copy / Paste",
    upload: "Document Upload",
    record: "Recording Session",
    export: "Summary Export",
    summary: "Summary Export",
  };

  const sourceTypes = {
    text: "Upload",
    upload: "Upload",
    record: "Recording",
    export: "Summary",
    summary: "Summary",
  };

  const baseStructure = {
    title: selectedDocumentType,
    description: descriptions[option] || "Document",
    type: selectedDocumentType,
    typeId: selectedDocumentTypeId,
    sourceType: sourceTypes[option] || "",
    createdAt: new Date(startTime),
    documentDate: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
    // Upload/paste/summary submission time is not an encounter time.
    startTime: option === "record"
      ? new Date(startTime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      : "",
    endTime: "",
    status: "processing",
    modifiedAt: new Date(),
  };

  if (selectedCategoryId) {
    baseStructure.category = selectedCategoryId;
  }

  if (option === "text") {
    baseStructure.processed_text = { summary: "", humanReview: false };
  }

  if (option === "export" || option === "summary") {
    baseStructure.processed_text = { summary: "", humanReview: false };
    baseStructure.isExport = true;
  }

  // Create empty sections for all sections of this document type
  if (documentSections) {
    const emptySections = createEmptySections(documentSections);
    Object.assign(baseStructure, emptySections);
  }

  return baseStructure;
}

export function optimizePatientHistory(patientDocuments, maxDocs) {
  const selected = selectHistoryDocuments(patientDocuments);
  return (maxDocs === undefined ? selected : selected.slice(0, maxDocs)).map(projectHistoryDocument);
}

/**
 * Generic retry wrapper for network requests
 * @param {Function} requestFn - Function that returns a Promise
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {Function} onRetry - Optional callback called on each retry
 * @returns {Promise} The result of the successful request
 */
export async function withRetry(requestFn, maxRetries = 3, onRetry = null) {
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      return await requestFn();
    } catch (error) {
      retryCount++;
      if (retryCount < maxRetries) {
        if (onRetry) {
          onRetry(retryCount, maxRetries, error);
        }
        // Wait before retrying (exponential backoff)
        const waitTime = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      } else {
        throw error;
      }
    }
  }
}
